import {
  Policy,
  PolicyState,
  ApplicationFormData,
} from '@policyfly/protobuf'
import { InputData_Protobuf_Access, InputData_Protobuf_Bucket } from '@policyfly/protobuf/pfschema'
import { getOneofKindValue } from '@policyfly/utils/protobuf'
import { addUUIDs, UUID_REGEX } from '@policyfly/utils/uuid'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

import { useDiffStore } from '@/stores/diff'

import { devtools } from '@/plugins/devtools/api'
import { hotReloadStore } from '@/utils/build'
import {
  SCRATCH_KEY,
  APPLICATION_RESPONSES_KEY,
  cachedPolicyState,
  cachedDrillFieldInfo,
  readFieldInfo,
  writeFieldInfo,
  getWritableArray,
  unwrapApplicationFormResponse,
  drillFieldInfo,
  ScheduleItemFieldInfo,
  ApplicationFormDataFieldInfo,
} from '@/utils/protobuf'

import type { DrilledFieldInfo } from '@/utils/protobuf'
import type {
  ApplicationFormList,
  ApplicationFormObject,
  ApplicationFormResponse,
  Coverage,
  Quote,
} from '@policyfly/protobuf'
import type { ScheduleItem } from '@policyfly/protobuf/pfschedule'
import type { InputData_Protobuf } from '@policyfly/protobuf/pfschema'
import type { AnyObject } from '@policyfly/types/common'
import type { DeepReadonly, ComputedRef, WritableComputedRef } from 'vue'

export type ReadonlyPolicyState = DeepReadonly<PolicyState>
export type ReadonlyCoverage = DeepReadonly<Coverage>
export type ReadonlyPolicy = DeepReadonly<Policy>
export type ReadonlyApplicationFormData = DeepReadonly<ApplicationFormData>
type ReadonlyQuote = DeepReadonly<Quote>

interface DataBuckets {
  policyState: PolicyState
  applicationFormData: ApplicationFormData
}

export interface ProtobufValueOptions {
  /**
   * Which bucket to use if the data does not have a bucket set.
   */
  defaultBucket: InputData_Protobuf_Bucket
  /**
   * If using `InputData_Protobuf_Bucket.BUCKET_SCHEDULE` it will use this item
   * as the root value.
   * If not provided and the bucket tries to be accessed then this will warn.
   */
  scheduleItem?: ScheduleItem | DeepReadonly<ScheduleItem> | null
}

export interface ProtobufStore {
  /**
   * The currently loaded {@link PolicyState} for the active Application.
   * Is an empty state if no Application is loaded.
   * This is readonly and cannot be modified, see {@link policyStateWorkingCopy}.
   */
  policyState: WritableComputedRef<ReadonlyPolicyState>
  /**
   * The currently loaded {@link Policy} for the active Policy.
   * Is an empty state if no Application is loaded.
   * This is readonly and cannot be modified, see {@link policyStateWorkingCopy}.
   */
  policy: WritableComputedRef<ReadonlyPolicy>
  /**
   * The currently loaded {@link ApplicationFormData} for the active Application.
   * Is an empty data bucket no Application is loaded.
   * This is readonly and cannot be modified, see {@link applicationFormDataWorkingCopy}.
   */
  applicationFormData: WritableComputedRef<ReadonlyApplicationFormData>
  /**
   * Editable copies that can be used for forms or other data entry.
   * These changes are not persisted until requested and can be discarded.
   *
   * **NOTE: These copies should not be sent directly to the GRPC services, instead use `getProcessedData`.**
   */
  workingCopies: WritableComputedRef<DataBuckets | null>
  /**
   * Either the working copy or, if that doesn't exist, the saved `policyState`.
   */
  currentPolicyState: ComputedRef<ReadonlyPolicyState | PolicyState>
  /**
   * Either the working copy or, if that doesn't exist, the saved `applicationFormData`.
   */
  currentApplicationFormData: ComputedRef<ReadonlyApplicationFormData | ApplicationFormData>
  /**
   * The currently selected quotes from the `currentPolicyState`.
   */
  selectedQuotes: ComputedRef<ReadonlyQuote[]>
  /**
   * Copies any data to working copies so they can be edited.
   */
  createWorkingCopies (): void
  /**
   * Updates working copies with the provided replacements.
   * Ensures properties such as the scratch data on the `policyState` are safely preserved.
   *
   * @param copies The updated {@link WorkingCopies} to update the current working copies with.
   * @param save If `true` will also save a readonly copy to the saved items. Default: `false`.
   */
  updateWorkingCopies (copies: DataBuckets, save?: boolean): void
  /**
   * Gets the `FieldInfo` for a provided protobuf path and data as if you were reading a value.
   * This does not create new fields and only reads existing fields, the same as `getValue`.
   */
  getReadFieldInfo (
    data: InputData_Protobuf,
    options: ProtobufValueOptions
  ): DrilledFieldInfo | null
  /**
   * Gets a value from the specified `bucket` at the provided data.
   *
   * Changing the `bucket` affects where the data is retrieved from, see {@link InputData_Protobuf_Bucket}.
   */
  getValue<T>(
    data: InputData_Protobuf,
    options: ProtobufValueOptions
  ): T | undefined
  /**
   * Sets a value in the specified `bucket` at the provided path.
   * Warns if the value could not be set.
   */
  setValue (
    data: InputData_Protobuf,
    value: unknown,
    options: ProtobufValueOptions
  ): void
  /**
   * Removes items from the array at the provided data, the same as `Array.prototype.splice`.
   * Removes every item from `start` onwards if `deleteCount` is not provided.
   * Warns if there is no array at the provided data.
   */
  spliceArrayValue (
    data: InputData_Protobuf,
    start: number,
    deleteCount: number | undefined,
    options: ProtobufValueOptions
  ): void
  /**
   * Moves an item within the array at the provided data from one index to another.
   * Warns if there is no array at the provided data.
   */
  moveArrayValue (
    data: InputData_Protobuf,
    from: number,
    to: number,
    options: ProtobufValueOptions
  ): void
  /**
   * Clones and processes the working copies so that all fields are correctly filled
   * and ready to be sent to a GRPC service.
   */
  getProcessedData (): DataBuckets
  /**
   * @deprecated Only used for sending policy state to django endpoints.
   *
   * Processes the `policyStateWorkingCopy` and converts it to a `Blob`.
   */
  serializePolicyState (): Blob
  /**
   * Overwrites saved data with the equivalent working copies.
   */
  saveCopies (): void
  /**
   * Either loads the passed in data or, if not provided, saves the working copies.
   * Useful for loading the result of a "submit" endpoint with optional data being returned.
   *
   * If neither of these are available will show a console error.
   */
  loadData (
    updatedData: (Omit<DataBuckets, 'applicationFormData'> & { applicationFormData: ApplicationFormData | undefined }) | undefined
  ): void
  /**
   * Loads the current diff state from the {@link useDiffStore diffStore} and creates a working copy.
   * This mainly is used to add removed items from arrays so they can be displayed.
   */
  loadDiffWorkingCopy (): void
  /**
   * Resets to the initial state.
   */
  $reset (): void
}

export const useProtobufStore = defineStore('protobuf', (): ProtobufStore => {
  const getPolicyStateInfo = cachedDrillFieldInfo.bind(null, false)
  const setPolicyStateInfo = cachedDrillFieldInfo.bind(null, true)

  const getScheduleItemInfo = drillFieldInfo.bind(null, false, ScheduleItemFieldInfo)
  const setScheduleItemInfo = drillFieldInfo.bind(null, true, ScheduleItemFieldInfo)

  /**
   * Drills from the root of the ApplicationFormData, but returns paths relative to its responses,
   * the same as the paths used by `BUCKET_APPLICATION` data.
   */
  function drillApplicationFormDataInfo (create: boolean, path: string, applicationFormData: AnyObject): DrilledFieldInfo | null {
    const info = drillFieldInfo(create, ApplicationFormDataFieldInfo, `${APPLICATION_RESPONSES_KEY}.${path}`, applicationFormData)
    if (!info) return null
    const prefixLength = `${APPLICATION_RESPONSES_KEY}.`.length
    return {
      ...info,
      paths: {
        resolved: info.paths.resolved.slice(prefixLength),
        diff: info.paths.diff.slice(prefixLength),
      },
    }
  }
  const getApplicationFormDataInfo = drillApplicationFormDataInfo.bind(null, false)
  const setApplicationFormDataInfo = drillApplicationFormDataInfo.bind(null, true)

  const policyStateInternal = ref<ReadonlyPolicyState>(cachedPolicyState(readonly(PolicyState.create())))
  const policyState: ProtobufStore['policyState'] = computed({
    get: (): ReadonlyPolicyState => policyStateInternal.value,
    set: (val: PolicyState | ReadonlyPolicyState): void => {
      policyStateInternal.value = cachedPolicyState(readonly(val))
    },
  })

  const policyInternal = ref<ReadonlyPolicy>(readonly(Policy.create()))
  const policy: ProtobufStore['policy'] = computed({
    get: (): ReadonlyPolicy => policyInternal.value,
    set: (val: Policy | ReadonlyPolicy): void => {
      policyInternal.value = readonly(val)
    },
  })

  const applicationFormDataInternal = ref<ReadonlyApplicationFormData>(readonly(ApplicationFormData.create()))
  const applicationFormData: ProtobufStore['applicationFormData'] = computed({
    get: (): ReadonlyApplicationFormData => applicationFormDataInternal.value,
    set: (val: ApplicationFormData): void => {
      applicationFormDataInternal.value = readonly(val)
    },
  })

  const workingCopiesInternal = ref<DataBuckets | null>(null)
  const workingCopies: ProtobufStore['workingCopies'] = computed({
    get: (): DataBuckets | null => workingCopiesInternal.value,
    set: (val: DataBuckets | null): void => {
      workingCopiesInternal.value = val && {
        policyState: cachedPolicyState(val.policyState),
        applicationFormData: val.applicationFormData,
      }
    },
  })

  const currentPolicyState: ProtobufStore['currentPolicyState'] = computed(() => {
    return workingCopies.value?.policyState ?? policyState.value
  })

  const currentApplicationFormData: ProtobufStore['currentApplicationFormData'] = computed(() => {
    return workingCopies.value?.applicationFormData ?? applicationFormData.value
  })

  const selectedQuotes: ProtobufStore['selectedQuotes'] = computed(() => {
    const selectedUuids = currentPolicyState.value.quotes?.selected ?? []
    const firstQuote = currentPolicyState.value.quotes?.quotes[0]
    const quotes = selectedUuids.map((uuid) => currentPolicyState.value.quotes?.quotes.find((quote) => quote.uuid4 === uuid))
    return (!quotes.length && firstQuote ? [firstQuote] : quotes).map((quote) => readonly(quote!))
  })

  const createWorkingCopies: ProtobufStore['createWorkingCopies'] = () => {
    workingCopies.value = {
      policyState: PolicyState.clone(policyState.value as PolicyState),
      applicationFormData: ApplicationFormData.clone(applicationFormData.value as ApplicationFormData),
    }
  }

  function mergeApplicationFormDataResponse (workingResponse: ApplicationFormResponse, updatedResponse: ApplicationFormResponse): void {
    if (workingResponse.value.oneofKind !== updatedResponse.value.oneofKind) {
      console.warn(`Trying to merge different response data structures: ${updatedResponse} => ${workingResponse}`)
      return
    }

    switch (workingResponse.value.oneofKind) {
      case 'object':
        mergeApplicationFormDataObject(workingResponse.value.object.fields, (updatedResponse.value as { object: ApplicationFormObject }).object.fields)
        break
      case 'list': {
        const workingItems = workingResponse.value.list.items
        const responseItems = (updatedResponse.value as { list: ApplicationFormList }).list.items
        responseItems.forEach((responseItem, index) => {
          if (!workingItems[index]) {
            workingItems[index] = responseItem
          }
          mergeApplicationFormDataResponse(workingItems[index], responseItem)
        })
        workingResponse.value.list.items.splice(responseItems.length)
        break
      }
      default:
        workingResponse.value = updatedResponse.value
    }
  }
  function mergeApplicationFormDataObject (working: Record<string, ApplicationFormResponse>, updated: Record<string, ApplicationFormResponse> | undefined): void {
    if (!updated) return
    for (const [k, updatedResponse] of Object.entries(updated)) {
      if (!(k in working)) {
        working[k] = updatedResponse
        continue
      }

      mergeApplicationFormDataResponse(working[k], updatedResponse)
    }
  }
  function mergeApplicationFormData (workingCopy: ApplicationFormData, updatedData: ApplicationFormData): ApplicationFormData {
    mergeApplicationFormDataObject(workingCopy.responses, updatedData.responses)
    return workingCopy
  }

  const updateWorkingCopies: ProtobufStore['updateWorkingCopies'] = (updatedState, save = false) => {
    if (!workingCopies.value) {
      console.warn('Trying to update working copies but they do not exist. Make sure `useProtobufCopy` has been run on this page.')
      return
    }
    const clone = PolicyState.clone(updatedState.policyState)
    clone[SCRATCH_KEY] = workingCopies.value.policyState[SCRATCH_KEY]
    workingCopies.value = {
      policyState: clone,
      applicationFormData: mergeApplicationFormData(workingCopies.value.applicationFormData, updatedState.applicationFormData),
    }
    if (save) {
      policyState.value = PolicyState.clone(updatedState.policyState)
      applicationFormData.value = ApplicationFormData.clone(updatedState.applicationFormData)
    }
  }

  const getReadFieldInfo: ProtobufStore['getReadFieldInfo'] = (data, options) => {
    const bucket = data.bucket ?? options.defaultBucket
    switch (bucket) {
      case InputData_Protobuf_Bucket.BUCKET_PS_SAVED:
        return getPolicyStateInfo(data.path, policyState.value)
      case InputData_Protobuf_Bucket.BUCKET_SCRATCH:
        return getPolicyStateInfo(`${SCRATCH_KEY}.${data.path}`, currentPolicyState.value)
      case InputData_Protobuf_Bucket.BUCKET_SCHEDULE:
        if (!options.scheduleItem) {
          console.warn(`Trying to read schedule value but no item passed: ${data.path}`)
          return null
        }
        return getScheduleItemInfo(data.path, options.scheduleItem)
      case InputData_Protobuf_Bucket.BUCKET_APPLICATION:
        return getApplicationFormDataInfo(data.path, currentApplicationFormData.value)
      default:
        return getPolicyStateInfo(data.path, currentPolicyState.value)
    }
  }
  const getValue: ProtobufStore['getValue'] = (data, options) => {
    const info = getReadFieldInfo(data, options)
    if (!info) return undefined
    return readFieldInfo(info)
  }

  function getWriteFieldInfo (data: InputData_Protobuf, options: ProtobufValueOptions): DrilledFieldInfo | null {
    const bucket = data.bucket ?? options.defaultBucket
    switch (bucket) {
      case InputData_Protobuf_Bucket.BUCKET_PS_SAVED:
        console.warn(`Trying to set value on saved PolicyState: ${data.path}`)
        return null
      case InputData_Protobuf_Bucket.BUCKET_SCRATCH:
        if (!workingCopies.value) {
          console.warn(`Trying to set value but no working copies exist. Make sure \`useProtobufCopy\` has been run on this page: ${data.path}`)
          return null
        }
        return setPolicyStateInfo(`${SCRATCH_KEY}.${data.path}`, workingCopies.value.policyState)
      case InputData_Protobuf_Bucket.BUCKET_SCHEDULE:
        if (!options.scheduleItem) {
          console.warn(`Trying to read schedule value but no item passed: ${data.path}`)
          return null
        }
        return setScheduleItemInfo(data.path, options.scheduleItem)
      case InputData_Protobuf_Bucket.BUCKET_APPLICATION:
        if (!workingCopies.value) {
          console.warn(`Trying to set value but no working copies exist. Make sure \`useProtobufCopy\` has been run on this page: ${data.path}`)
          return null
        }
        return setApplicationFormDataInfo(data.path, workingCopies.value.applicationFormData)
      default:
        if (!workingCopies.value) {
          console.warn(`Trying to set value but no working copies exist. Make sure \`useProtobufCopy\` has been run on this page: ${data.path}`)
          return null
        }
        return setPolicyStateInfo(data.path, workingCopies.value.policyState)
    }
  }
  const setValue: ProtobufStore['setValue'] = (data, value, options) => {
    if (data.access === InputData_Protobuf_Access.ACCESS_READONLY) {
      console.warn(`Trying to set value with readonly access: ${data.path}`)
      return
    }

    const info = getWriteFieldInfo(data, options)
    if (!info) {
      devtools.configWarning({
        type: 'input',
        title: `Failed to set path ${data.path} to value ${value}`,
      })
      return
    }
    writeFieldInfo(info, value)
  }

  function getWritableArrayValue (data: InputData_Protobuf, options: ProtobufValueOptions): unknown[] | null {
    if (data.access === InputData_Protobuf_Access.ACCESS_READONLY) {
      console.warn(`Trying to modify array with readonly access: ${data.path}`)
      return null
    }
    const info = getWriteFieldInfo(data, options)
    const arr = info && getWritableArray(info)
    if (!arr) {
      devtools.configWarning({
        type: 'input',
        title: `Failed to modify array at path ${data.path}`,
      })
      return null
    }
    return arr
  }
  const spliceArrayValue: ProtobufStore['spliceArrayValue'] = (data, start, deleteCount, options) => {
    const arr = getWritableArrayValue(data, options)
    if (!arr) return
    if (deleteCount === undefined) arr.splice(start)
    else arr.splice(start, deleteCount)
  }
  const moveArrayValue: ProtobufStore['moveArrayValue'] = (data, from, to, options) => {
    const arr = getWritableArrayValue(data, options)
    if (!arr) return
    const [item] = arr.splice(from, 1)
    arr.splice(to, 0, item)
  }

  function stripInvalidResponses (responses: Record<string, ApplicationFormResponse>): void {
    for (const [k, v] of Object.entries(responses)) {
      switch (v.value.oneofKind) {
        case 'number':
          if (v.value.number === '') {
            delete responses[k]
            continue
          }
          break
        case 'object':
          stripInvalidResponses(v.value.object.fields)
          continue
        case 'list':
          for (const item of v.value.list.items) {
            // NOTE: This only handles lists of nested objects
            if (item.value.oneofKind === 'object') {
              stripInvalidResponses(item.value.object.fields)
            }
          }
          continue
      }
      const oneofValue = getOneofKindValue(v.value)
      if (oneofValue === null || oneofValue === undefined) {
        delete responses[k]
      }
    }
  }
  function cleanFormData (formData: ApplicationFormData): ApplicationFormData {
    const clone = ApplicationFormData.clone(formData)
    stripInvalidResponses(clone.responses)
    return clone
  }
  const getProcessedData: ProtobufStore['getProcessedData'] = () => {
    if (!workingCopies.value) throw new Error('No working copies available')
    const clone = PolicyState.clone(workingCopies.value.policyState)
    clone.resourceVersion = policyState.value.resourceVersion
    return {
      policyState: addUUIDs(clone),
      applicationFormData: cleanFormData(workingCopies.value.applicationFormData),
    }
  }

  const serializePolicyState: ProtobufStore['serializePolicyState'] = () => {
    return new Blob([PolicyState.toBinary(getProcessedData().policyState) as BlobPart])
  }

  const saveCopies: ProtobufStore['saveCopies'] = () => {
    const processedData = getProcessedData()
    policyState.value = processedData.policyState
    applicationFormData.value = processedData.applicationFormData
  }

  const loadData: ProtobufStore['loadData'] = (updatedData) => {
    if (updatedData) {
      policyState.value = updatedData.policyState
      applicationFormData.value = updatedData.applicationFormData ?? ApplicationFormData.create()
    } else if (workingCopies.value) {
      saveCopies()
    } else {
      console.error('No PolicyState available to be loaded')
    }
  }

  const diffStore = useDiffStore()
  const loadDiffWorkingCopy: ProtobufStore['loadDiffWorkingCopy'] = () => {
    createWorkingCopies()
    const removed = diffStore.diffData?.removed ?? []
    // create any missing array items
    for (const item of removed) {
      const parts = item.k.split('.')
      const prefixParts = []
      const suffixParts = []
      let uuid = ''
      for (const part of parts) {
        if (UUID_REGEX.test(part)) {
          uuid = part
          continue
        }
        if (uuid) suffixParts.push(part)
        else prefixParts.push(part)
      }
      if (uuid) {
        const arrK = prefixParts.join('.')
        const arr = getValue<AnyObject[]>({ path: arrK }, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
        if (!arr) setValue({ path: arrK }, [], { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
        if (!arr || !arr.some((v) => v.uuid4 === uuid)) {
          const len = arr?.length ?? 0
          setValue({ path: [arrK, len, 'uuid4'].join('.') }, uuid, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
        }
      }
    }
    // set all removed values
    for (const item of removed) {
      const k = item.k.split('.')
        .map((v) => UUID_REGEX.test(v) ? `(uuid4=${v})` : v)
        .join('.')
      setValue({ path: k }, item.v, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
    }
    // re-inject removed ApplicationFormData responses into the working copy
    const formDataRemoved = diffStore.formDataDiffData?.removed ?? []
    for (const item of formDataRemoved) {
      setValue({ path: item.k }, unwrapApplicationFormResponse(item.v), { defaultBucket: InputData_Protobuf_Bucket.BUCKET_APPLICATION })
    }
  }

  const $reset: ProtobufStore['$reset'] = () => {
    policyState.value = PolicyState.create()
    workingCopies.value = null
    policy.value = Policy.create()
  }

  return {
    policyState,
    policy,
    applicationFormData,
    workingCopies,
    currentPolicyState,
    currentApplicationFormData,
    selectedQuotes,
    createWorkingCopies,
    updateWorkingCopies,
    getReadFieldInfo,
    getValue,
    setValue,
    spliceArrayValue,
    moveArrayValue,
    getProcessedData,
    serializePolicyState,
    saveCopies,
    loadData,
    loadDiffWorkingCopy,
    $reset,
  }
})

hotReloadStore(useProtobufStore)
